import { jsx } from 'panel';
import camelCase from 'lodash/camelCase';
import { EntryPart, FiltersOperator, SelectorTypeDefinitions } from 'common/widgets/query-entry-section/types';
import { ButtonEl } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { QueryBlockEl } from 'common/widgets/query-block';
import { MenuItemType, TypecastPillAction } from 'common/widgets/query-block/properties-menu/types';
import { RowFilters, RowFiltersPrefix } from 'common/widgets/query-entry-section/components/filters';
import { serializeAttributes } from 'common/util';
import { ResourceType } from 'common/report/constants';
import { SelectEl } from 'common/components/select';
import { SUPPORTED_BEHAVIORAL_AGGREGATIONS } from 'common/report/util/cohorts/utils';
import { QueryEntrySectionEl } from '../base/query-entry-section-el';
import { BaseEntryRowEl } from '../base/template';
import { isBehavioralFilterNegated, transformBehavioralFilterToBehavioralEntry } from '../behavior/utils';
import { BehavioralAction } from './types';
export function CohortGroupEntryRowEl(_ref) {
  let {
    index,
    entry,
    section
  } = _ref;
  const hasFilters = this.entry.filters.length > 0;
  const isStagingFilter = Boolean(this.section.state.placeholders.get(this.index));
  const hasExtendedOperators = this.showExtendedLogicalOperators("filtersOperator") || this.showExtendedLogicalOperators("behavioralFiltersOperator");
  const filterQueryBlockConfig = {
    elref: "behavioralFilterBlock",
    type: "properties",
    theme: ButtonTheme.Tertiary,
    'allow-typecast-pill-action': TypecastPillAction.TriggerTutorial,
    'selector-icon': "plus",

    /** @todo [pablo] enable custom event creation from this menu */
    'hide-edit-controls': true,
    'data-group-id': section.attr("data-group-id"),
    'enable-enhanced-list-filters': section.attr("enable-enhanced-list-filters"),
    'enable-workspace-filter-restrictions': section.attr("enable-workspace-filter-restrictions"),
    'exclude-custom-events': section.attr("exclude-custom-events"),
    'inline-cohorts-override': section.attr("inline-cohorts-override"),
    'project-id': section.attr("project-id"),
    'input-only': true,
    'show-special-dataset-property': section.attr("show-special-dataset-property"),
    'hide-special-date-property-unit': section.attr("hide-special-date-property-unit"),
    'show-special-date-property': section.attr("show-special-date-property"),
    'track-name': section.attr("track-name"),
    'selector-type': "button",
    'user-id': section.attr("user-id"),
    disabled: section.attr("disabled"),
    'workspace-id': section.attr("workspace-id"),
    'show-all-tab': true,
    'hide-events': true,
    // defines what type of properties this dropdown will render
    'menu-item-types': [MenuItemType.Events, MenuItemType.UserProperties, MenuItemType.UserCustomProperties, MenuItemType.LookupTableProperties, MenuItemType.Cohorts],
    'all-data-groups': section.attr("data-groups-enabled"),
    'exclude-dimension-properties': section.attr("exclude-dimension-properties"),
    'include-custom-properties': section.attr("include-custom-properties"),
    'project-has-cohorts': section.attr("project-has-cohorts"),
    'project-has-people': section.attr("project-has-people"),
    'support-b2b-cohorts': section.attr("support-b2b-cohorts")
  };

  const selectorRenderer = () => jsx(null, null, jsx("div", {
    sel: '.mp-query-entry-selector-title'
  }, this.getGroupTitle()), !isStagingFilter && !hasFilters && jsx(null, null, jsx(QueryBlockEl, {
    key: 'entry-user-filter-selector',
    attrs: Object.assign(Object.assign({}, filterQueryBlockConfig), {
      label: "Filter",
      autofocus: this.section.state.stagedRows.length === 1 && this.index === 0
    }),
    on: {
      select: ev => this.handlePropertyItemClick(ev)
    }
  })));

  const bodyRowRenderer = () => jsx(null, null, jsx("div", {
    sel: '.mp-query-entry-row.for-filters'
  }, jsx(RowFilters, {
    el: section,
    entry: entry,
    index: index,
    source: EntryPart.Filters,
    handleUpdate: ev => this.handleFilterSectionUpdate(EntryPart.Filters, ev.detail),
    filtersOperators: this.showExtendedLogicalOperators("filtersOperator") && [FiltersOperator.And, FiltersOperator.Or, FiltersOperator.AndAny, FiltersOperator.OrAll]
  }), BehavioralFrequencyFilters(this), hasFilters && jsx(null, null, jsx("div", {
    sel: '.mp-query-entry.is-add-row',
    class: {
      'is-disabled': this.isPlaceholder()
    }
  }, jsx("div", {
    sel: '.entry-selector-prefix',
    class: {
      'for-extended-operators': hasExtendedOperators
    }
  }), jsx(QueryBlockEl, {
    key: 'entry-filter-selector',
    attrs: Object.assign(Object.assign({}, filterQueryBlockConfig), {
      label: "Filter"
    }),
    on: {
      select: ev => this.handlePropertyItemClick(ev)
    }
  })))));
  /** grouping operator control (AND/OR) */


  const suffixRenderer = !entry.groupingOperator ? null : () => jsx("div", {
    sel: '.mp-query-entry-group-operator',
    class: {
      ["is-".concat(entry.groupingOperator)]: true
    }
  }, jsx(ButtonEl, {
    attrs: {
      elref: "entryGroupOperator",
      theme: entry.groupingOperator === FiltersOperator.And ? ButtonTheme.Primary : ButtonTheme.Tertiary,
      role: "button"
    },
    on: {
      click: () => this.handleEntryGroupingOperatorClick()
    }
  }, entry.groupingOperator));
  return jsx(BaseEntryRowEl, {
    index: index,
    el: section,
    entry: entry,
    rowRenderer: {
      prefixRenderer: null,
      selectorRenderer,
      bodyRowRenderer,
      suffixRenderer
    },
    hideActions: true,
    isPlaceholder: this.isPlaceholder()
  });
}

function BehavioralFrequencyFilters(row) {
  var _a, _b;

  const {
    placeholders
  } = row.section.state;
  const showPlaceholder = (_a = placeholders.get(row.index)) === null || _a === void 0 ? void 0 : _a.includes(EntryPart.BehavioralFrequencyFilters);
  const filters = row.section.helpers.getEntryFilters(row.index)[EntryPart.BehavioralFrequencyFilters] || [];

  if (filters.length === 0) {
    return null;
  }

  return jsx(QueryEntrySectionEl, {
    key: "entry-".concat(EntryPart.BehavioralFrequencyFilters, "-section"),
    class: {
      'indent-filters': true
    },
    attrs: serializeAttributes(Object.assign(Object.assign({}, row.section.helpers.getNestedSectionAttrs()), {
      elref: camelCase("entry-".concat(EntryPart.BehavioralFrequencyFilters, "-section")),
      'data-groups-enabled': false,
      'allow-cohort-filter': false,
      'can-rename-step': false,
      'project-has-people': false,
      'project-has-cohorts': false,
      'disable-add-entry': true,
      'row-label-type': "none",

      /** funnel based behaviors do not allow aggregations as funnel steps, only frequency */
      'metrics-mode': row.entry.behavioralFiltersOperator !== FiltersOperator.Then,
      'include-custom-properties': "all",
      'hide-edit-controls': row.section.attr("hide-edit-controls"),
      'row-types': [{
        value: ResourceType.Event,
        includeDateRange: true
      }, {
        value: SelectorTypeDefinitions.EventPropertyAggregation,
        includeDateRange: true
      }],
      entries: filters.map(filter => transformBehavioralFilterToBehavioralEntry(filter, row.entry.behavioralFiltersOperator)),
      'event-aggregator-functions': SUPPORTED_BEHAVIORAL_AGGREGATIONS,
      'property-aggregator-functions': SUPPORTED_BEHAVIORAL_AGGREGATIONS
    })),
    props: {
      /** renders the AND/OR switch for metric entries (for clarity, the entry type in this renderer function is not a filter type) */
      prefixRenderer: _ref2 => {
        let {
          index: filterIndex,
          entry
        } = _ref2;
        return BehavioralRowFiltersPrefix({
          filterIndex,
          filter: filters[filterIndex],
          filtersOperator: row.entry.behavioralFiltersOperator,
          handleChange: (operator, action) => row.handleBehavioralFilterOperatorChange({
            operator,
            action,
            entry: entry,
            filterIndex
          }),
          isReadOnlyMode: row.section.attr("readonly"),
          includeExtendedOperators: row.showExtendedLogicalOperators("behavioralFiltersOperator") && filters.length >= 3,
          isNegated: isBehavioralFilterNegated(entry)
        });
      },
      isStagingRow: showPlaceholder && Object.assign(Object.assign({}, placeholders.get(row.index)[1]), {
        type: ResourceType.Event,
        behavioralFiltersOperator: ((_b = row.entry) === null || _b === void 0 ? void 0 : _b.behavioralFiltersOperator) || FiltersOperator.And
      })
    },
    on: {
      abortStage: () => row.handleAbortStage(),
      update: ev => row.handleFilterSectionUpdate(EntryPart.BehavioralFrequencyFilters, ev.detail)
    }
  });
}
/**
 * For behaviors using extended logical operators (and any, or all), we display a different UI
 * where these options are displayed in cascading menu, for every other case we display a flat list
 */


export function BehavioralRowFiltersPrefix(_ref3) {
  let {
    filtersOperator,
    filter,
    filterIndex,
    handleChange: _handleChange,
    isReadOnlyMode,
    includeExtendedOperators,
    isNegated
  } = _ref3;
  return jsx(null, null, RowFiltersPrefix({
    filtersOperator,
    filter,
    filterIndex,
    handleChange: item => _handleChange(item.value, null),
    isReadOnlyMode,
    forBehaviors: true,
    filtersOperators: includeExtendedOperators ? [FiltersOperator.And, FiltersOperator.Or, FiltersOperator.AndAny, FiltersOperator.OrAll, FiltersOperator.Then] : [FiltersOperator.And, FiltersOperator.Or, FiltersOperator.Then]
  }), jsx(SelectEl, {
    sel: '.entry-selector-behavioral-action',
    attrs: {
      elref: "behavioralFilterActionSelect",
      theme: ButtonTheme.Select,
      'menu-width': "100",
      options: [BehavioralAction.Did, BehavioralAction.DidNot],
      'never-show-caret': true,
      'selected-option': isNegated ? BehavioralAction.DidNot : BehavioralAction.Did
    },
    on: {
      change: ev => _handleChange(null, ev.detail.value)
    }
  }));
}